import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type FileFormatApiRow, updateFileFormat } from '@/api/file-formats';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileFormatForm, type FileFormatFormHandle } from './file-format-form';
import type { FileFormatFormValues } from './file-format-form.types';
import { emptyFormValues, rowToFormValues, toUpdatePayload } from './file-format-form.utils';
import { useFileFormatSubmit } from './use-file-format-submit';

interface EditFileFormatModalProps {
  open: boolean;
  row: FileFormatApiRow | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const EditFileFormatModal: React.FC<EditFileFormatModalProps> = ({
  open,
  row,
  onOpenChange,
  onSuccess,
}) => {
  const [values, setValues] = useState<FileFormatFormValues>(() => emptyFormValues());
  const formRef = useRef<FileFormatFormHandle>(null);

  const handleSuccess = useCallback(() => {
    onOpenChange(false);
    onSuccess();
  }, [onOpenChange, onSuccess]);

  const perform = useCallback(
    async (vals: FileFormatFormValues) => {
      if (!row) throw new Error('No file format selected for editing');
      return updateFileFormat(row.id, toUpdatePayload(vals));
    },
    [row]
  );

  const { isLoading, fieldErrors, isValid, reportJsonValidity, handleSubmit, reset } =
    useFileFormatSubmit({
      values,
      validateJsonEditors: () => formRef.current?.validateJsonEditors() ?? true,
      perform,
      onSuccess: handleSuccess,
    });

  useEffect(() => {
    if (open && row) {
      setValues(rowToFormValues(row));
      reset();
    }
  }, [open, row, reset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit File Format</DialogTitle>
            <DialogDescription>
              Update format details, metadata, and parsing configuration.
            </DialogDescription>
          </DialogHeader>
          <FileFormatForm
            ref={formRef}
            values={values}
            onChange={setValues}
            fieldErrors={fieldErrors}
            initialCode={row?.code ?? ''}
            readOnlyScope={{
              scope: values.scope,
              party: row?.party ?? null,
            }}
            onJsonValidityChange={reportJsonValidity}
          />
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !row || !isValid}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
